'use client'

import React, { useEffect, useState } from 'react'

import useRouter from '@/utils/Router'
import useStateManagement from '@/hooks/stateManagement/useStateManagement'
import { useParams } from '@/utils/navigation'
import store from '@/store'
import { snapshot } from 'valtio'

import uploadThumbnail from '@/components/ProductProfile/utils/saveData/uploadThumbnail'
import resizeToTile from '@/components/ProductProfile/utils/saveData/resizeToTile'
import mapProductProfileToSave from '@/components/ProductProfile/utils/saveData/mapProductProfileToSave'
import mapSections from '@/components/ProductProfile/utils/loadData/mapSections'


import createSections from '@/components/ProductProfile/utils/saveData/createSections'

import mapSectionToSave from '@/components/ProductProfile/utils/saveData/mapSectionToSave'
import convertSingleToBundle from '@/components/Products/utils/convertSingleToBundle'

import getProductBySKU from '@/services/ProductService/getProductBySKU'
import getProductProfile from '@/services/ProductService/getProductProfile'
import getViewProfiles from '@/services/ProductService/getViewProfiles'
import getShapeProfiles from '@/services/ProductService/getShapeProfiles'
import getPricingList from '@/services/PricingServices/getPricingList'
import deleteProductProfile from '@/services/ProductService/deleteProductProfile'
import createProductProfile from '@/services/ProductService/createProductProfile'



import Form, { FormGeneratorHandles } from '@/components/Form'
import getProductProfileFormItems from '@/components/ProductProfile/forms/productProfileFormItems'
import Heading from '@/components/Heading/Heading'
import ImageComponent from '@/components/UiElements/ImageComponent'
import cleanAllTargetObjects from '@/components/ProductProfile/utils/drawSection/cleanAllTargetObjects'
import Alert from '@/components/UiElements/Alert'
import { closeNotif, startNotif } from '@/components/DynamicForm/util/notifier'
import updateProfileIndex from '@/services/ProductService/updateProfileIndex'
import Modal from '@/components/Modal/Modal'
import ViewProfileForm from '@/components/ViewProfile/ViewProfileForm'
import PricingForm from '@/components/Pricing/Components/PricingForm'
import ShapeProfileForm from '@/components/ShapeProfile/ShapeProfileForm'



export default function ProductProfileV2() {

	const { sku } = useParams()
	const router = useRouter()


	const formRef = React.useRef<FormGeneratorHandles>(null)



	const [product, setProduct] = useState<any | null>(null)
	const [loaded, setLoaded] = useState(false)
	const [selectedProfileIndex, _setSelectedProfileIndex] = useState(0)
	const selectedProfileIndexRef = React.useRef(0)
	const setSelectedProfileIndex = (index: number) => {
		selectedProfileIndexRef.current = index
		_setSelectedProfileIndex(index)
	}
	const [tabs, setTabs] = useState<{ content: string; name: string }[]>([])
	const [newProfile, setNewProfile] = useState(false)
	const [updatingProfileOrder, setUpdatingProfileOrder] = useState(false)
	const [viewProfileModalOpen, setViewProfileModalOpen] = useState(false)
	const [pricingModalOpen, setPricingModalOpen] = useState(false)
	const [shapeProfileModalOpen, setShapeProfileModalOpen] = useState(false)

	const productProfileFormItems = React.useMemo(
		() => getProductProfileFormItems({
			onAddViewProfile: () => setViewProfileModalOpen(true),
			onAddPricing: () => setPricingModalOpen(true),
			onAddShapeProfile: () => setShapeProfileModalOpen(true),
		}),
		[]
	)



	const bundle = new URLSearchParams(window.location.search).get('bundle')



	const loadProduct = async () => {

		setLoaded(false)

		const productDataList: any = await getProductBySKU(sku)
		const productData: any | null = productDataList.find((p: any) => p.sku === sku) || null

		if (productData) {

			formRef.current?.updateValue('product', productData)
			formRef.current?.updateValue('callToAction', createProfile)

			const profileList: { content: string; name: string }[] = productData?.product_profiles?.map((profile: any) => ({
				content: profile.id,
				name: profile.name,
				action: () => handleDeleteProductProfile(profile.id, profile.name),
				actionIcon: '',
			}))//.sort((a: any, b: any) => a.content.localeCompare(b.content))

			setProduct(productData)
			setTabs(profileList)

			const currentIndex = selectedProfileIndexRef.current
			const productProfileId = profileList[currentIndex]?.content || profileList[0]?.content
			if (productProfileId) {
				const resolvedIndex = profileList.findIndex((p) => p.content === productProfileId)
				if (resolvedIndex >= 0) setSelectedProfileIndex(resolvedIndex)
				await setProductProfileForm(productProfileId)
			} else {
				handleAddNewProductProfile()
				setLoaded(true)
			}

		} else {
			console.log("no profile")
			setLoaded(true)

		}
	}

	const setProductProfileForm = async (productProfileId: string | null) => {

		if (!productProfileId) return



		setLoaded(false)
		const profileData = await getProductProfile(productProfileId)
		if (profileData) {
			console.log("----", profileData?.env_param)
			const sectionData = profileData?.shape_profile?.sections || []
			const mappedSections = await mapSections(sectionData)

			// Determine profile view early
			const vpData = profileData?.view_profile as any
			const profileView = profileData?.env_param?.profile_view || vpData?.data?.profile_view || '3D'

			formRef.current?.updateValue('sections', mappedSections)

			const nullUUID = '00000000-0000-0000-0000-000000000000'
			const shapeProfileId = profileData?.shape_profile?.id
			const hasValidShapeProfile = shapeProfileId && shapeProfileId !== nullUUID
			formRef.current?.updateValue('profileData', hasValidShapeProfile ? profileData?.shape_profile : null)
			if (hasValidShapeProfile) {
				formRef.current?.updateValue('shapeProfile', {
					value: profileData.shape_profile.id,
					label: profileData.shape_profile.name,
					data: profileData.shape_profile,
				})
			} else {
				formRef.current?.updateValue('shapeProfile', null)
			}
			formRef.current?.updateValue('lights', profileData?.env_param)
			formRef.current?.updateValue('priceModel', { value: profileData?.pricing?.id })
			formRef.current?.updateValue('name', profileData?.name)
			formRef.current?.updateValue('id', productProfileId)

			// Load profile type from env_param, default to 'laser'
			const profileType = profileData?.env_param?.profile_type || 'laser'
			formRef.current?.updateValue('profileType', { value: profileType, label: profileType.charAt(0).toUpperCase() + profileType.slice(1) })

			formRef.current?.updateValue('profileView', { value: profileView, label: profileView })

			if (profileView === '2D') {
				// 2D: set default target object dimensions (actual values come per-section from calibration)
				formRef.current?.updateValue('targetObjectWidth', 200)
				formRef.current?.updateValue('targetObjectHeight', 200)
			} else {
				// 3D: load view profile if available
				if (vpData) {
					formRef.current?.updateValue('viewProfile', {
						value: vpData.id,
						label: vpData.name,
						data: vpData,
					})
					const vpViewUrl = vpData?.data?.view_url
					if (vpViewUrl) {
						formRef.current?.updateValue('glb', {
							string: vpViewUrl,
							url: vpViewUrl,
							loaded: true,
							name: 'view-profile.glb',
							size: 0,
							type: 'model/gltf-binary',
							data: null,
						})
					}
				} else if (profileData?.shape_profile?.['3d_model_url']) {
					// Fallback for legacy profiles
					const modelUrl = profileData.shape_profile['3d_model_url']
					formRef.current?.updateValue('glb', {
						string: modelUrl,
						url: modelUrl,
						loaded: true,
						name: 'legacy-model.glb',
						size: 0,
						type: 'model/gltf-binary',
						data: null,
					})
				}
			}


		}
		window.scrollTo(0, 0)
		setLoaded(true)

	}


	const {
		runSteps: createProfile,
	} = useStateManagement({
		uploadSectionImages: {
			main: async (state, form, notif) => {
				if (form?.profileView?.value !== '2D') return []
				const imageList: string[] = []
				const total = form?.sections?.length || 0
				for (let index = 0; index < total; index++) {
					const section = form?.sections[index]?.formData
					const sectionImage = section?.sectionImage
					notif(`Uploading section image ${index + 1}/${total}`)
					if (sectionImage?.url) {
						imageList.push(sectionImage.url)
					} else if (sectionImage?.data) {
						const url = await uploadThumbnail(sectionImage.data, form.product, index, 'product_images')
						imageList.push((url as string) || '')
					} else if (sectionImage?.string) {
						imageList.push(sectionImage.string)
					} else {
						imageList.push('')
					}
				}
				return imageList
			},
			startMessage: () => 'Uploading section images...',
			errorMessage: () => 'Upload section images failed :(',
		},
		uploadThumbnail: {
			main: async (state, form, notif) => {

				const is2D = form?.profileView?.value === '2D'
				const total = form?.sections?.length
				const thumbList = []

				if (is2D) {
					// For 2D profiles, the thumbnail is the uploaded section/product image.
					for (let index = 0; index < total; index++) {
						notif(`Uploading thumbnail ${index + 1}/${total}`)
						thumbList.push(state?.uploadSectionImages?.[index] || '')
					}
					return thumbList
				}

				for (let index = 0; index < total; index++) {

					const section = form?.sections[index]?.formData
					console.log('section ---->', section)
					const image = section.thumbnail

					notif(`Uploading thumbnail ${index + 1}/${total}`)

					if (image.url) {
						thumbList.push(image.url)
					} else {
						const thumb = image ? await uploadThumbnail(image.data, form.product, index) : ''
						thumbList.push(thumb)
					}
				}

				return thumbList

			},
			startMessage: () => 'Uploading thumbnails...',
			errorMessage: () => 'Upload thumbnails failed :(',
		},
		uploadMask: {
			main: async (state, form, notif) => {


				const total = form?.sections?.length || 0
				const maskList: Record<string, string> = {}

				for (let index = 0; index < total; index++) {
					const section = form?.sections?.[index]?.formData as
						| { mask?: { url?: string; data?: any } }
						| undefined


					const image = section?.mask
					notif(`Uploading mask ${index + 1}/${total}`)

					if (typeof image?.url === 'string') {
						maskList[index] = image.url
					} else if (image?.data) {
						const thumb = await uploadThumbnail(image.data, form.product, index, 'section_masks')
						maskList[index] = typeof thumb === 'string' ? thumb : ''
					} else {
						maskList[index] = ''
					}
				}

				return maskList


			},
			startMessage: () => 'Uploading masks...',
			errorMessage: () => 'Upload masks failed :(',
		},


		uploadMaterialFiles: {
			main: async (state, form, notif) => {

				const total = form?.sections?.length || 0
				const materialList: Record<string, string> = {}

				for (let index = 0; index < total; index++) {
					const section = form?.sections?.[index]?.formData as
						| { materialType?: any; materialSource?: { mode?: string; file?: { url?: string; data?: any } | null } }
						| undefined

					const materialType = section?.materialType?.value || section?.materialType
					const image = section?.materialSource?.file

					// Only a custom material in pattern mode carries a file; anything
					// else must not hold a stale URL.
					if (materialType !== 'custom' || section?.materialSource?.mode !== 'pattern') {
						materialList[index] = ''
						continue
					}

					notif(`Uploading material pattern ${index + 1}/${total}`)

					if (typeof image?.url === 'string') {
						materialList[index] = image.url
					} else if (image?.data) {
						const tile = await resizeToTile(image.data)
						const uploaded = await uploadThumbnail(tile, form.product, index, 'material_patterns')
						materialList[index] = typeof uploaded === 'string' ? uploaded : ''
					} else {
						materialList[index] = ''
					}
				}

				return materialList

			},
			startMessage: () => 'Uploading material patterns...',
			errorMessage: () => 'Upload material patterns failed :(',
		},


		createSections: {
			main: async (state, form, notif) => {
				const storeSnap = snapshot(store)
				const sections = form?.sections?.map((s: { formData: any }) => s.formData)
				const mappedSections = mapSectionToSave(
					sections,
					state,
					storeSnap.companySetting?.machine,
					form?.profileView?.value === '2D'
				)
				const totalSections = mappedSections.length
				const sectionList = []

				for (let index = 0; index < totalSections; index++) {
					const section = mappedSections[index]
					notif(`Create section ${index + 1}/${totalSections}`)
					const sectionId = await createSections(section)
					sectionList.push(sectionId)
				}

				return sectionList
			},
			startMessage: () => 'Creating sections...',
			errorMessage: () => 'Create sections failed :(',
		},


		createProductProfile: {
			main: async (state, form) => {

				//const productProfileId = form?.product ? form?.product?.product_profiles?.[selectedProfileIndex]?.id : null

				let productProfile = await mapProductProfileToSave(form, state)
				const update = !!form.id
				console.log(productProfile)

				return await createProductProfile(productProfile, update)

			},
			startMessage: () => 'Creating Product Profile...',
			successMessage: () => 'Product Profile Created :)',
			errorMessage: () => 'Create Profile failed :(',
		},
		updatePage: {
			main: async (state, form) => {
				console.log(state, form)
				await loadProduct()
			},
			startMessage: () => 'Creating Product Profile...',
			successMessage: () => 'Product Profile Created :)',
			errorMessage: () => 'Create Profile failed :(',
		}
	})



	useEffect(() => {
		if (!loaded) {
			loadProduct()
		}

	}, [sku])

	const resetNewProductProfileForm = () => {
		formRef.current?.updateValue('sections', [])
		formRef.current?.updateValue('profileData', null)
		formRef.current?.updateValue('shapeProfile', null)
		formRef.current?.updateValue('viewProfile', null)
		formRef.current?.updateValue('glb', null)
		formRef.current?.updateValue('priceModel', null)
		formRef.current?.updateValue('name', null)
		formRef.current?.updateValue('id', null)
		formRef.current?.updateValue('profileType', { value: 'laser', label: 'Laser' })
		formRef.current?.updateValue('profileView', { value: '3D', label: '3D' })
		formRef.current?.updateValue('productImage', null)
		formRef.current?.updateValue('targetObjectWidth', 200)
		formRef.current?.updateValue('targetObjectHeight', 200)

		cleanAllTargetObjects()
	}

	const handleSelectProductProfile = async (index: number) => {
		setSelectedProfileIndex(index)

		if (newProfile && index === tabs.length) {
			resetNewProductProfileForm()
			return
		}

		cleanAllTargetObjects()

		const productProfileId = tabs?.[index]?.content
		await setProductProfileForm(productProfileId)
	}



	const handleAddNewProductProfile = () => {


		setSelectedProfileIndex(tabs.length)
		setNewProfile(true)
		resetNewProductProfileForm()
	}


	const handleDeleteProductProfile = async (profileIdParam?: string, profileNameParam?: string) => {
		// Use passed parameters (from tab action) or fall back to current selection (from menu)
		const profileId = profileIdParam ?? tabs[selectedProfileIndex]?.content
		const profileName = profileNameParam ?? tabs[selectedProfileIndex]?.name

		if (!profileId || !profileName) {
			console.error('No profile to delete')
			return
		}
		Alert.showAlert({
			title: 'Delete Profile',
			isCancelable: true,
			danger: true,
			button: 'Delete!',
			message:
				<div className={'flex flex-col items-center justify-center '}>
					Are you sure you want to delete the profile &quot;{profileName}&quot;?
				</div>,
			callBack: async (ok) => {
				if (ok) {
					const toast = startNotif('Deleting product profile...')
					const res = await deleteProductProfile(profileId)

					if (!res) {
						closeNotif(toast, 'Failed to delete product profile.', {
							type: 'error'
						})
					} else {
						closeNotif(toast, 'Product profile deleted successfully.')
						// Reload the page to ensure fresh state
						await loadProduct()
					}
				}

			},
		})
	}



	const handleOnChange = (data: any) => {
		const currentProfileView = data?.profileView?.value || null

		// 3D: bridge view profile's view_url to glb
		if (currentProfileView !== '2D' && data?.viewProfile) {
			const viewUrl = data?.viewProfile?.data?.data?.view_url
			const currentGlb = data?.glb
			if (viewUrl && (!currentGlb || currentGlb.string !== viewUrl)) {
				formRef.current?.updateValue('glb', {
					string: viewUrl,
					url: viewUrl,
					loaded: true,
					name: 'view-profile.glb',
					size: 0,
					type: 'model/gltf-binary',
					data: null,
				})
			}
		}
	}


	const handleChangeSortProfiles = async (updated: any[]) => {
		setUpdatingProfileOrder (true)
		const updatedRequest = updated.map((tab: any, index: number) => ({
			id: tab.content,
			order_index: index
		}))
		const notif = startNotif('Updating profiles order...')
		const update = await updateProfileIndex({
			updates: updatedRequest
		})

		if (update?.message) {
			const currentSelectedProfile = tabs[selectedProfileIndex]
			const index = updated.findIndex(tab => tab.content === currentSelectedProfile.content)
			setSelectedProfileIndex(index)
			setTabs(updated)
			closeNotif(notif, 'Profiles order updated.' )
		} else {
			closeNotif(notif, 'Failed to update profiles order.', { type: 'error' })
		}
		setUpdatingProfileOrder (false)
		console.log("update", update)

	}


	// Refresh a Select's options and auto-select the just-created item.
	// The item is matched by id (preferred) or, as a fallback, by name (most recent).
	const selectCreatedOption = (field: string, options: any[], created?: { id?: any; name?: string }) => {
		// Touch the formItem so the Select refetches its options (new item now exists)
		formRef.current?.updateForm(field, '_refresh', Date.now())
		const match =
			(created?.id != null ? options.find((o) => o.value === created.id) : undefined) ||
			[...options].reverse().find((o) => o.label === created?.name)
		// changeInput (not updateValue) so onChange fires — bridges the GLB for view profiles
		if (match) formRef.current?.changeInput(field, match)
	}

	const handleViewProfileCreated = async (created?: { id?: any; name?: string }) => {
		setViewProfileModalOpen(false)
		const profiles = await getViewProfiles()
		const options = (profiles || []).map((item: any) => ({ label: item?.name, value: item?.id, data: item }))
		selectCreatedOption('viewProfile', options, created)
	}

	const handlePricingCreated = async (created?: { id?: any; name?: string }) => {
		setPricingModalOpen(false)
		// getPricingList already returns { label, value, data } options
		const options = (await getPricingList()) || []
		selectCreatedOption('priceModel', options, created)
	}

	const handleShapeProfileCreated = async (created?: { id?: any; name?: string }) => {
		setShapeProfileModalOpen(false)
		const profiles = await getShapeProfiles()
		const options = (profiles || []).map((item: any) => ({ label: item?.name, value: item?.id, data: item }))
		selectCreatedOption('shapeProfile', options, created)
	}

	(window as any).getForm = () => formRef.current?.getData()



	return (
		<div className="flex h-full w-full bg-white gap-[32px]">
			<div className="flex-grow w-full">
				<Heading
					title={
						<div className={'flex flex-row gap-md items-center justify-start'}>
							{product?.['name'] || '...'}
							<span className="font-icon-light text-gray-300 select-none"></span>
							<span className={'inline-flex gap-xs text-gray-300 !text-text-lg select-none'}>Single</span>
						</div>
					}
					description={product?.['variant_name']}
					parent={{
						title: `${bundle ? 'Main Product' : 'Products'}`,
						url: `/products${bundle ? `/bundle/${bundle}` : ''}`,
					}}
					options={[
						{
							title: 'Convert to Bundle',
							onClick: async () => {
								if (product) {
									store.loadingPage = 'Converting to bundle, please wait...'
									const response = await convertSingleToBundle(product)
									if (response) {
										router.push(`/products/bundle/${product?.['sku']}`)
									}
									store.loadingPage = null
								}
							},
							icon: '',
							disabled: !product,
						},
						{
							title: 'Download Profile',
							icon: '',
							disabled: true,

						},
						{
							title: 'Delete Profile',
							icon: '',
							disabled: !product,
							onClick: handleDeleteProductProfile,
							className: "text-red-500 hover:bg-red-100"
						},
						{
							separator: true,
						},
						{
							title: 'Add New Profile',
							icon: '+',
							disabled: !product,
							onClick: handleAddNewProductProfile,

						},

					]}
					tab={product ? {
						onSelectedChange: handleSelectProductProfile,
						tabs: [
							...tabs,
							...(newProfile
								? [{
									content: '__new_product_profile__',
									name: 'New',
								}]
								: []),
						],
						selectedTab: selectedProfileIndex || 0,
						changeSort: updatingProfileOrder || newProfile ? null : handleChangeSortProfiles
					} : null}

				>
					{product && (
						<div className={'flex flex-row gap-xxl items-center'}>
							<div>
								<ImageComponent
									src={product?.['2d_image_url']}
									style={{ height: '100px', width: '100px' }}
								/>
							</div>
							<div className="flex-col justify-start items-start gap-2 inline-flex">
								<div className="text-neutral-600 text-sm font-normal flex flex-row">
									<span className={'text-gray-400 w-[60px]'}>Sku: </span>
									{product?.['sku']}
								</div>
								<div className="text-neutral-600 text-sm font-normal capitalize flex flex-row">
									<span className={'text-gray-400 w-[60px]'}>Variant: </span>
									{product?.['variant_name'] || <span className={'text-gray-400'}>-</span>}
								</div>
								<div className="text-neutral-600 text-sm font-normal flex flex-row">
									<span className={'text-gray-400 w-[60px]'}>Price:</span>
									${Number(product?.['price']).toFixed(2)}
								</div>
							</div>
						</div>
					)}
				</Heading>

				<div className={'px-xxl gap-lg flex flex-row x-[100%] pb-[200px]'}>
					<div className={'w-[37px]'} />
					<div className={`w-[calc(100%-61px)]`}>
						<div className={`grid gap-xl relative grid-cols-12 ${!loaded ? 'hidden' : ''}`}>
							<Form
								ref={formRef}
								items={productProfileFormItems}
								defaultValues={{}}
								onChange={handleOnChange}
							/>
						</div>
						<div className={`flex justify-center items-center p-xl text-gray-400 ${loaded ? 'hidden' : ''}`}>
							loading...
						</div>
					</div>
				</div>

			</div>

			<Modal
				isOpen={viewProfileModalOpen}
				onClose={() => setViewProfileModalOpen(false)}
				title="Add view profile"
				closeOnOutsideClick={false}
			>
				<ViewProfileForm
					profile={null}
					onUpdateSuccess={handleViewProfileCreated}
					defaultSku={sku as string}
					onCancel={() => setViewProfileModalOpen(false)}
				/>
			</Modal>

			<Modal
				isOpen={pricingModalOpen}
				onClose={() => setPricingModalOpen(false)}
				title="New Price"
				closeOnOutsideClick={false}
			>
				<PricingForm
					model={{}}
					onUpdateSuccess={handlePricingCreated}
					closeModal={() => setPricingModalOpen(false)}
				/>
			</Modal>

			<Modal
				isOpen={shapeProfileModalOpen}
				onClose={() => setShapeProfileModalOpen(false)}
				title="Add shape profile"
				closeOnOutsideClick={false}
			>
				<ShapeProfileForm profile={null} onUpdateSuccess={handleShapeProfileCreated} />
			</Modal>
		</div>
	)
}
